import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Tags, FlaskConical, Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/lib/toast';
import { ProductsInSheet, type ProductsInTarget } from '@/components/catalog/ProductsInSheet';
import { ImportCsvDialog, downloadCsv } from '@/components/catalog/ImportCsvDialog';
import { useAuthStore } from '@/stores/auth.store';
import { PageHeader } from '@/components/ui/page-header';
import { SearchInput } from '@/components/ui/search-input';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { useTranslation } from 'react-i18next';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

interface Brand {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  logoUrl?: string | null;
  isActive: boolean;
  _count?: { products: number };
}

const defaultForm = {
  name: '',
  slug: '',
  description: '',
  logoUrl: '',
};

export function BrandsPage() {
  const { t } = useTranslation('products');
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('inventory:create') || hasPermission('inventory:*');
  const canUpdate = hasPermission('inventory:update') || hasPermission('inventory:*');
  const canDelete = hasPermission('inventory:delete') || hasPermission('inventory:*');

  const { data: tenantData } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
    staleTime: 30_000,
  });
  const isTestMode: boolean = tenantData?.testMode ?? false;

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editBrand, setEditBrand] = useState<Brand | null>(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string; products: number }>({ open: false, id: '', name: '', products: 0 });
  const [productsIn, setProductsIn] = useState<ProductsInTarget | null>(null);
  const [nameError, setNameError] = useState('');
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());
  const nameRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    setNameError('');
    if (dialogOpen && editBrand) {
      setForm({
        name: editBrand.name ?? '',
        slug: editBrand.slug ?? '',
        description: editBrand.description ?? '',
        logoUrl: editBrand.logoUrl ?? '',
      });
      setSlugManuallyEdited(true);
    } else if (dialogOpen) {
      setForm({ ...defaultForm });
      setSlugManuallyEdited(false);
    }
  }, [dialogOpen, editBrand]);

  const { data: brands, isLoading } = useQuery<Brand[]>({
    queryKey: ['brands'],
    queryFn: async () => {
      const res = await api.get('/brands');
      return res.data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name,
        slug: form.slug || undefined,
        description: form.description || undefined,
        logoUrl: form.logoUrl || undefined,
      };
      if (editBrand) {
        const { data } = await api.patch(`/brands/${editBrand.id}`, payload);
        return data;
      }
      const { data } = await api.post('/brands', payload);
      return data;
    },
    onSuccess: () => {
      toast.success(editBrand ? 'Brand updated' : 'Brand created');
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setDialogOpen(false);
    },
    onError: (error: unknown) => {
      const message = getApiErrorMessage(error, 'Failed to save brand');
      // POS-I370: a name clash is shown under the Name field.
      if (/already exists/i.test(message)) {
        setNameError(message);
        nameRef.current?.focus();
      }
      toast.error(message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/brands/${id}`),
    onSuccess: () => {
      toast.success('Brand deleted');
      queryClient.invalidateQueries({ queryKey: ['brands'] });
    },
    onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to delete brand')),
  });

  const brandsList: Brand[] = Array.isArray(brands) ? brands : [];
  const needle = search.trim().toLowerCase();
  const filtered = brandsList.filter((b) => b.name.toLowerCase().includes(needle) || (b.slug ?? '').toLowerCase().includes(needle));

  function handleNameChange(name: string) {
    const updates: Partial<typeof form> = { name };
    if (!slugManuallyEdited) {
      updates.slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }
    setForm({ ...form, ...updates });
  }

  function handleSlugChange(slug: string) {
    setSlugManuallyEdited(true);
    setForm({ ...form, slug });
  }

  return (
    <div className="space-y-6 page-enter">
      <PageHeader
        title="Brands"
        subtitle="Manage product brands (Samsung, Apple, etc.)"
        action={(
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => downloadCsv('/brands/export.csv', 'brands_' + new Date().toISOString().slice(0, 10) + '.csv').catch(() => toast.error('Export failed'))}>
              <Download className="me-2 h-4 w-4" />
              Export
            </Button>
            {canCreate && (
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="me-2 h-4 w-4" />
                Import
              </Button>
            )}
            {canCreate && (
              <Button variant="gradient" onClick={() => { setEditBrand(null); setDialogOpen(true); }}>
                <Plus className="me-2 h-4 w-4" />
                Add Brand
              </Button>
            )}
          </div>
        )}
      />

      {isTestMode && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-text">
          <FlaskConical className="h-4 w-4 shrink-0" />
          <span><strong>Test Mode:</strong> viewing test brands.</span>
        </div>
      )}

      <div className="mb-4">
        <SearchInput
          placeholder="Search brands..."
          value={search}
          onChange={(value) => setSearch(value)}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <SkeletonTable cols={4} />
          ) : filtered.length === 0 && brandsList.length > 0 ? (
            <EmptyState
              icon={Tags}
              title={t('noBrandsMatch', { search: search.trim() })}
              action={{ label: t('clearSearch'), onClick: () => setSearch('') }}
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={Tags}
              title="No brands yet"
              description="Create your first brand to organize products by manufacturer."
            />
          ) : (
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('tbl.brand')}</Th>
                    <Th>{t('description')}</Th>
                    <Th className="text-end">{t('title')}</Th>
                    <Th className="text-end">{t('actionsColumn')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {filtered.map((brand) => (
                    <Tr key={brand.id}>
                      <Td label={t('tbl.brand')} className="td-name">
                        <div className="flex items-center gap-3">
                          {brand.logoUrl && !failedLogos.has(brand.id) ? (
                            <img
                              src={brand.logoUrl}
                              alt={brand.name}
                              className="h-8 w-8 rounded object-contain"
                              onError={() => setFailedLogos((prev) => new Set(prev).add(brand.id))}
                            />
                          ) : (
                            <span className="flex h-8 w-8 items-center justify-center rounded bg-muted">
                              <Tags className="h-4 w-4 text-muted-foreground" />
                            </span>
                          )}
                          <span className="font-medium text-foreground">{brand.name}</span>
                        </div>
                      </Td>
                      <Td label={t('description')} className="text-muted-foreground">{brand.description || '—'}</Td>
                      <Td label={t('title')} className="text-end text-muted-foreground">
                        {(brand._count?.products ?? 0) > 0 ? (
                          <button
                            type="button"
                            className="font-medium text-primary-text underline underline-offset-2 hover:no-underline"
                            onClick={() => setProductsIn({ kind: 'brand', id: brand.id, name: brand.name })}
                          >
                            {brand._count?.products}
                          </button>
                        ) : 0}
                      </Td>
                      <Td label={t('actionsColumn')} className="td-actions">
                        <TableRowActions
                          actions={[
                            {
                              key: 'edit',
                              label: `Edit ${brand.name}`,
                              icon: Edit,
                              onSelect: () => { setEditBrand(brand); setDialogOpen(true); },
                              permission: canUpdate,
                              primary: true,
                            },
                            {
                              key: 'delete',
                              label: `Delete ${brand.name}`,
                              icon: Trash2,
                              onSelect: () => setDeleteConfirm({ open: true, id: brand.id, name: brand.name, products: brand._count?.products ?? 0 }),
                              permission: canDelete,
                              variant: 'destructive',
                            },
                          ]}
                        />
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
          )}
        </CardContent>
      </Card>

      <FormDialog
        open={dialogOpen}
        title={editBrand ? 'Edit Brand' : 'Add Brand'}
        width="32rem"
        submitLabel={editBrand ? 'Save Changes' : 'Create Brand'}
        isPending={saveMutation.isPending}
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.name.trim()) return;
          saveMutation.mutate();
        }}
        onClose={() => setDialogOpen(false)}
      >
        <div>
          <Label htmlFor="brand-name">Name</Label>
          <Input
            id="brand-name"
            ref={nameRef}
            value={form.name}
            onChange={(e) => { setNameError(''); handleNameChange(e.target.value); }}
            placeholder="e.g. Samsung"
            aria-invalid={!!nameError}
          />
          {nameError && <p role="alert" className="mt-1 text-xs text-destructive-text">{nameError}</p>}
        </div>
        <div>
          <Label htmlFor="brand-slug">Slug</Label>
          <Input
            id="brand-slug"
            value={form.slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder="samsung"
          />
        </div>
        <div>
          <Label htmlFor="brand-logo">Logo URL</Label>
          <Input
            id="brand-logo"
            value={form.logoUrl}
            onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
            placeholder="https://..."
          />
        </div>
        <div>
          <Label htmlFor="brand-desc">Description</Label>
          <Textarea
            id="brand-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
          />
        </div>
      </FormDialog>

      <ProductsInSheet target={productsIn} onClose={() => setProductsIn(null)} />
      <ImportCsvDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        noun="brands" nounOne="brand"
        basePath="/brands"
        onImported={() => { queryClient.invalidateQueries({ queryKey: ['brands'] }); queryClient.invalidateQueries({ queryKey: ['products'] }); }}
      />

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete Brand"
        description={`Are you sure you want to delete "${deleteConfirm.name}"? Products will keep their data but lose this brand association.${deleteConfirm.products > 0 ? ' ' + t('brandDeleteInUse', { count: deleteConfirm.products }) : ''}`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate(deleteConfirm.id, {
            onSuccess: () => setDeleteConfirm({ open: false, id: '', name: '', products: 0 }),
          });
        }}
        onClose={() => setDeleteConfirm({ open: false, id: '', name: '', products: 0 })}
      />
    </div>
  );
}
